// copied from: https://git.hubteam.com/HubSpot/ui-addon-conversations-settings/blob/3c89aee1836bb1efd278f39f1bb316d4f77c6c81/static/js/users-and-teams/components/UsersAndTeamsSelect.tsx
import { useEffect, useState } from 'react';
import { Map as ImmutableMap, List } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Avatar } from 'avatar-components/Avatar';
import UISelect from 'UIComponents/input/UISelect';
import virtualizedMenuRenderer from 'UIComponents/input/utils/virtualizedMenuRenderer';
import { apiTeamSelectionToTree } from '../../team/operators/apiTeamSelectionToTree';
import { deselectNodeOnTree } from '../../team/operators/deselectNodeOnTree';
import { selectNodeOnTree } from '../../team/operators/selectNodeOnTree';
import { treeToApiTeamSelection } from '../../team/operators/treeToApiTeamSelection';
import { treeToSelectedValues } from '../../team/operators/treeToSelectedValues';
import { filterOutSearchResults } from '../operators/filterOutSearchResults';
import { getTruncatedGroupOptions } from '../operators/getTruncatedGroupOptions';
import AgentSelectItem from './AgentSelectItem';
import ChatFlowInboxAccessTooltip from './tooltips/ChatFlowInboxAccessTooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const renderAvatar = email => {
  if (!email) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      width: '16px',
      height: '16px'
    },
    children: /*#__PURE__*/_jsx(Avatar, {
      size: "xxs",
      hubSpotUserEmail: email
    })
  });
};
const getGroupedOptionsFromLists = (usersOptions, teamsOptions, hideUserSectionTooltip, inboxId) => {
  const groupOptions = [{
    text: I18n.text('routing-ui.users-and-teams.groups.team'),
    options: teamsOptions
  }, {
    text: I18n.text('routing-ui.users-and-teams.groups.user'),
    dropdownText: typeof inboxId !== 'undefined' && !hideUserSectionTooltip ? /*#__PURE__*/_jsx(ChatFlowInboxAccessTooltip, {
      inboxId: inboxId.toString()
    }) : null,
    options: usersOptions.map(user => {
      user.avatar = renderAvatar(user.email);
      return user;
    })
  }];
  return groupOptions;
};
const DeprecatedUsersAndTeamsSelect = props => {
  const {
    'data-test-id': testId,
    disabled,
    dropDownFooter,
    error,
    handleChange,
    hideUserSectionTooltip = false,
    inboxId,
    isLoading,
    itemComponent,
    onMount,
    selectedTeamIds = List(),
    selectedUserIds = List(),
    teamsOptions,
    teamTree: teamTreeProps = ImmutableMap({}),
    usersOptions,
    valueComponent = AgentSelectItem
  } = props;
  const [teamTree, setTeamTree] = useState(() => apiTeamSelectionToTree(selectedTeamIds, teamTreeProps));
  const [options, setOptions] = useState(() => getGroupedOptionsFromLists(usersOptions, teamsOptions, hideUserSectionTooltip, inboxId));
  useEffect(() => {
    if (onMount) onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setTeamTree(apiTeamSelectionToTree(selectedTeamIds, teamTreeProps));
  }, [selectedTeamIds, teamTreeProps]);
  useEffect(() => {
    setOptions(getGroupedOptionsFromLists(usersOptions, teamsOptions, hideUserSectionTooltip, inboxId));
  }, [usersOptions, teamsOptions, inboxId, hideUserSectionTooltip]);
  const resolveTeamIds = currentSelectedTeamIds => {
    const currentTeamValues = treeToSelectedValues(teamTree).toSet();
    const newTeamValues = currentSelectedTeamIds.toSet();
    if (currentTeamValues.equals(newTeamValues)) {
      return currentSelectedTeamIds;
    }
    let teamIds = List();
    let updatedTree;
    if (newTeamValues.size > currentTeamValues.size) {
      updatedTree = selectNodeOnTree(newTeamValues.subtract(currentTeamValues).toList().get(0), teamTree);
    }
    if (currentTeamValues.size > newTeamValues.size) {
      updatedTree = deselectNodeOnTree(currentTeamValues.subtract(newTeamValues).toList().get(0), teamTree);
    }
    if (updatedTree) {
      teamIds = List(treeToApiTeamSelection(updatedTree));
    }
    return teamIds;
  };
  const mapGroupsToIds = () => {
    return {
      users: (usersOptions || []).map(el => el.value),
      teams: (teamsOptions || []).map(el => el.value)
    };
  };
  const handleSelectChange = e => {
    let userIds = List();
    let teamIds = List();
    const groupMap = mapGroupsToIds();
    e.target.value.forEach(value => {
      if (groupMap.users.includes(value)) {
        userIds = userIds.push(value);
      } else if (groupMap.teams.includes(value)) {
        teamIds = teamIds.push(value);
      }
    });
    teamIds = resolveTeamIds(teamIds);
    handleChange(userIds, teamIds);
  };
  const getSelectedValues = () => {
    const teamIds = treeToSelectedValues(teamTree);
    return teamIds.concat(selectedUserIds).toArray();
  };
  const getNumOptions = currentOptions => {
    return currentOptions.reduce((length, group) => length + group.options.length, 0);
  };
  const loadOptions = (input, callback, pagination = {
    offset: 0
  }) => {
    const PAGE_SIZE = 100;

    // filter options if there is a search input criteria
    const filteredOptions = filterOutSearchResults(options, input);
    const numOptions = getNumOptions(filteredOptions);
    const nextOffset = pagination.offset + PAGE_SIZE;
    const paginatedOptions = getTruncatedGroupOptions(filteredOptions, nextOffset);
    callback(null, {
      options: paginatedOptions,
      pagination: {
        hasMore: numOptions > nextOffset,
        length: numOptions,
        offset: nextOffset
      }
    });
  };
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const renderSelectedValues = getNumOptions(options) > 0;
  return /*#__PURE__*/_jsx(FormControl, {
    error: Boolean(error),
    validationMessage: error,
    children: /*#__PURE__*/_jsx(UISelect, {
      anchorType: "button",
      cache: false,
      className: "users-and-teams-select",
      "data-test-id": testId,
      disabled: disabled,
      dropdownFooter: dropDownFooter,
      filterOptions: false,
      isLoading: isLoading,
      itemComponent: itemComponent,
      loadOptions: loadOptions,
      menuRenderer: virtualizedMenuRenderer,
      menuStyle: {
        maxHeight: 320
      },
      multi: true,
      onChange: handleSelectChange
      // @ts-expect-error untyped for pagination
      ,
      onOpen: loadOptions,
      options: getGroupedOptionsFromLists(usersOptions, teamsOptions, hideUserSectionTooltip, inboxId),
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.users-and-teams.select.placeholder"
      }),
      value: renderSelectedValues ? getSelectedValues() : [],
      valueComponent: valueComponent
    })
  });
};
DeprecatedUsersAndTeamsSelect.displayName = 'DeprecatedUsersAndTeamsSelect';
export default DeprecatedUsersAndTeamsSelect;